/**
 * SEO Utilities for Store Locator and Location Landing Pages
 */

/**
 * Update meta tags for SEO
 * @param {Object} options - SEO options
 * @param {string} options.title - Page title
 * @param {string} options.description - Meta description
 * @param {string} options.keywords - Meta keywords
 * @param {string} options.image - OG image URL
 * @param {string} options.url - Canonical URL
 * @param {string} options.type - OG type (default: 'website')
 * @param {string} options.siteName - Site name for OG tags
 */
export function updateMetaTags(options) {
  const {
    title,
    description,
    keywords,
    image,
    url,
    type = 'website',
    siteName = 'Fly Social'
  } = options;

  // Update document title
  if (title) {
    document.title = title;
  }

  // Update or create meta description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    document.head.appendChild(metaDescription);
  }
  if (description) {
    metaDescription.content = description;
  }

  // Update or create meta keywords
  let metaKeywords = document.querySelector('meta[name="keywords"]');
  if (!metaKeywords) {
    metaKeywords = document.createElement('meta');
    metaKeywords.name = 'keywords';
    document.head.appendChild(metaKeywords);
  }
  if (keywords) {
    metaKeywords.content = keywords;
  }

  // Update or create canonical URL
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  if (url) {
    canonical.href = url;
  }

  // Update or create OG tags
  const ogTags = {
    'og:title': title,
    'og:description': description,
    'og:image': image,
    'og:url': url,
    'og:type': type,
    'og:site_name': siteName
  };

  Object.entries(ogTags).forEach(([property, content]) => {
    if (content) {
      let ogTag = document.querySelector(`meta[property="${property}"]`);
      if (!ogTag) {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', property);
        document.head.appendChild(ogTag);
      }
      ogTag.content = content;
    }
  });

  // Update or create Twitter Card tags
  const twitterTags = {
    'twitter:card': 'summary_large_image',
    'twitter:title': title,
    'twitter:description': description,
    'twitter:image': image
  };

  Object.entries(twitterTags).forEach(([name, content]) => {
    if (content) {
      let twitterTag = document.querySelector(`meta[name="${name}"]`);
      if (!twitterTag) {
        twitterTag = document.createElement('meta');
        twitterTag.name = name;
        document.head.appendChild(twitterTag);
      }
      twitterTag.content = content;
    }
  });
}

/**
 * Generate SEO title for store locator
 * @param {Object} workspaceData - Workspace data
 * @param {number} locationCount - Number of locations
 * @returns {string} SEO title
 */
export function generateStoreLocatorTitle(workspaceData, locationCount = 0) {
  const brandName = workspaceData?.name || 'Store';
  const locationText = locationCount > 0 ? ` (${locationCount} locations)` : '';
  return `${brandName} - Store Locator${locationText} | Find Our Locations`;
}

/**
 * Generate SEO description for store locator
 * @param {Object} workspaceData - Workspace data
 * @param {number} locationCount - Number of locations
 * @returns {string} SEO description
 */
export function generateStoreLocatorDescription(workspaceData, locationCount = 0) {
  const brandName = workspaceData?.name || 'Store';
  const description = workspaceData?.description || 'Find our locations and get directions to visit us.';
  const locationText = locationCount > 0 ? ` Visit any of our ${locationCount} locations` : ' Visit our location';
  
  return `${description}${locationText}. Get contact information, business hours, and directions.`;
}

/**
 * Generate SEO title for location landing page
 * @param {Object} location - Location data
 * @param {Object} workspaceData - Workspace data
 * @returns {string} SEO title
 */
export function generateLocationTitle(location, workspaceData) {
  const locationName = location?.name || location?.storeLocation || 'Location';
  const brandName = workspaceData?.name || '';
  const city = location?.city || '';
  const state = location?.state || '';
  
  let title = locationName;
  if (brandName && brandName !== locationName) {
    title = `${locationName} - ${brandName}`;
  }
  
  if (city && state) {
    title += ` in ${city}, ${state}`;
  } else if (city) {
    title += ` in ${city}`;
  }
  
  return title;
}

/**
 * Generate SEO description for location landing page
 * @param {Object} location - Location data
 * @param {Object} workspaceData - Workspace data
 * @returns {string} SEO description
 */
export function generateLocationDescription(location, workspaceData) {
  const locationName = location?.name || location?.storeLocation || 'Location';
  const brandName = workspaceData?.name || '';
  const description = location?.gmbProfile?.profile?.description || 
                     location?.gmb_shallow_profile_data?.description ||
                     workspaceData?.description ||
                     'Visit our location for quality service and exceptional care.';
  
  const address = location?.address || 
                  location?.gmbProfile?.storefrontAddress?.addressLines?.join(', ') ||
                  location?.gmbProfile?.address ||
                  '';
  
  const phone = location?.phone || '';
  const website = location?.website || '';
  
  let metaDescription = description;
  
  if (address) {
    metaDescription += ` Located at ${address}.`;
  }
  
  if (phone) {
    metaDescription += ` Call us at ${phone}.`;
  }
  
  if (website) {
    metaDescription += ` Visit our website for more information.`;
  }
  
  // Limit description to 160 characters for optimal SEO
  if (metaDescription.length > 160) {
    metaDescription = metaDescription.substring(0, 157) + '...';
  }
  
  return metaDescription;
}

/**
 * Generate keywords for SEO
 * @param {Object} location - Location data
 * @param {Object} workspaceData - Workspace data
 * @returns {string} Comma-separated keywords
 */
export function generateKeywords(location, workspaceData) {
  const keywords = [];
  
  // Add brand name
  if (workspaceData?.name) {
    keywords.push(workspaceData.name);
  }
  
  // Add location name
  if (location?.name || location?.storeLocation) {
    keywords.push(location.name || location.storeLocation);
  }
  
  // Add location details
  if (location?.city) keywords.push(location.city);
  if (location?.state) keywords.push(location.state);
  if (location?.country) keywords.push(location.country);
  
  // Add business category
  const category = location?.gmbProfile?.categories?.primaryCategory?.displayName ||
                   location?.gmb_shallow_profile_data?.primary_category;
  if (category) {
    keywords.push(category);
  }
  
  // Add common business keywords
  keywords.push('store locator', 'find location', 'business hours', 'contact information');
  
  return keywords.join(', ');
}

/**
 * Generate structured data for location
 * @param {Object} location - Location data
 * @param {Object} workspaceData - Workspace data
 * @returns {Object} Structured data object
 */
export function generateLocationStructuredData(location, workspaceData) {
  const locationName = location?.name || location?.storeLocation || 'Location';
  const brandName = workspaceData?.name || locationName;
  const description = location?.gmbProfile?.profile?.description || 
                     location?.gmb_shallow_profile_data?.description ||
                     workspaceData?.description ||
                     'Quality service and exceptional care';
  
  const address = location?.address || 
                  location?.gmbProfile?.storefrontAddress?.addressLines?.join(', ') ||
                  location?.gmbProfile?.address ||
                  '';
  
  const phone = location?.phone || '';
  const website = location?.website || '';
  const email = location?.email || '';
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": locationName,
    "description": description,
    "url": website,
    "telephone": phone,
    "email": email,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": address
    }
  };
  
  // Add logo if available
  const logo = workspaceData?.logo_original || 
               workspaceData?.logo_url || 
               workspaceData?.logo_dark || 
               workspaceData?.logo_transparent ||
               workspaceData?.logo;
  if (logo) {
    structuredData.logo = logo;
  }
  
  // Add image if available
  const image = location?.gmb_shallow_profile_data?.cover_photo ||
                location?.gmbProfile?.mediaItems?.coverPhoto?.googleUrl;
  if (image) {
    structuredData.image = image;
  }
  
  // Add opening hours if available
  const openingHours = location?.gmb_shallow_profile_data?.opening_hours ||
                       location?.gmbProfile?.regularHours?.periods;
  if (openingHours && openingHours.length > 0) {
    structuredData.openingHoursSpecification = openingHours.map(hours => ({
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": hours.day || hours.openDay,
      "opens": hours.hours?.split(' - ')[0] || hours.openTime,
      "closes": hours.hours?.split(' - ')[1] || hours.closeTime
    }));
  }
  
  // Add aggregate rating if available
  if (location?.averageRating && location?.reviewCount) {
    structuredData.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": location.averageRating,
      "reviewCount": location.reviewCount
    };
  }
  
  // Add sameAs (social media links) if available
  const socialLinks = [];
  if (location?.gmb_attributes) {
    location.gmb_attributes.forEach(attr => {
      if (attr.name && attr.value && attr.name.includes('url_')) {
        socialLinks.push(attr.value);
      }
    });
  }
  if (socialLinks.length > 0) {
    structuredData.sameAs = socialLinks;
  }
  
  return structuredData;
}

/**
 * Generate structured data for store locator
 * @param {Object} workspaceData - Workspace data
 * @param {Array} locations - Array of locations
 * @returns {Object} Structured data object
 */
export function generateStoreLocatorStructuredData(workspaceData, locations = []) {
  const brandName = workspaceData?.name || 'Store';
  const description = workspaceData?.description || 'Find our locations and get directions to visit us.';
  const website = workspaceData?.website || '';
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": brandName,
    "description": description,
    "url": website,
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `${brandName} Locations`,
      "itemListElement": locations.map((location, index) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "LocalBusiness",
          "name": location.name || location.storeLocation,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": location.address || 
                            location.gmbProfile?.storefrontAddress?.addressLines?.join(', ') ||
                            location.gmbProfile?.address
          },
          "telephone": location.phone,
          "url": location.website
        }
      }))
    }
  };
  
  // Add logo if available
  const logo = workspaceData?.logo_original || 
               workspaceData?.logo_url || 
               workspaceData?.logo_dark || 
               workspaceData?.logo_transparent ||
               workspaceData?.logo;
  if (logo) {
    structuredData.logo = logo;
  }
  
  return structuredData;
}

/**
 * Inject structured data into the page
 * @param {Object} structuredData - Structured data object
 */
export function injectStructuredData(structuredData) {
  // Remove existing structured data
  const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
  existingScripts.forEach(script => {
    if (script.textContent.includes('"@context":"https://schema.org"')) {
      script.remove();
    }
  });
  
  // Add new structured data
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(structuredData);
  document.head.appendChild(script);
}

/**
 * Clean up SEO meta tags when component is destroyed
 */
export function cleanupMetaTags() {
  // Remove custom meta tags we added
  const customMetaTags = document.querySelectorAll('meta[name="keywords"], meta[property^="og:"], meta[name^="twitter:"]');
  customMetaTags.forEach(tag => {
    if (tag.getAttribute('data-custom-seo') === 'true') {
      tag.remove();
    }
  });
  
  // Remove custom canonical link
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical && canonical.getAttribute('data-custom-seo') === 'true') {
    canonical.remove();
  }
  
  // Remove structured data
  const structuredDataScripts = document.querySelectorAll('script[type="application/ld+json"]');
  structuredDataScripts.forEach(script => {
    if (script.textContent.includes('"@context":"https://schema.org"')) {
      script.remove();
    }
  });
} 